import { Box, Container, Typography } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import IsMountedWrapper from '../../utils/functions/isMountedWrapper';
import { BookingStore } from '../../utils/stores/BookingStore';
import { ProductsStore } from '../../utils/stores/ProductsStore';
import BookingModal from '../bookings/BookingModal';

// Dark "action band" that continues the hero's ocean mood, so the booking tool reads as a distinct
// zone (dark) ahead of the light "Tours in Cairns" browsing section below.
const bandSx = {
    width: '100%',
    background: 'linear-gradient(180deg, #0c2731 0%, #0a1f28 100%)',
    py: { xs: 6, md: 8 },
} as const;

const eyebrowSx = {
    color: 'primary.main',
    letterSpacing: '0.18em',
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    mb: 1.25,
} as const;

const titleSx = {
    fontSize: { xs: 26, md: 34 },
    color: 'common.white',
    lineHeight: 1.15,
    letterSpacing: '-0.5px',
    fontWeight: 700,
    m: 0,
} as const;

const subtitleSx = {
    fontSize: { xs: 15, md: 17 },
    color: 'rgba(255,255,255,0.72)',
    mt: 1.25,
    maxWidth: 560,
    mx: 'auto',
} as const;

/**
 * Home-page booking CTA. Reuses the product-page availability box (date / time / fares / book now)
 * but lets the visitor pick the tour first. The tour selection drives the same global ProductsStore /
 * BookingStore used on the product page, so the availability + booking logic is shared, not duplicated.
 */
export default function HomeBookingCTA() {
    const productController = useContext(ProductsStore);
    const bookingController = useContext(BookingStore);
    const isMounted = IsMountedWrapper();
    const product = productController.product;

    // Mirror of ProductTemplate.updateBooking — seed the booking from the chosen tour's price options.
    useEffect(() => {
        if (!product?.productCode) return;
        bookingController.setBooking(
            (prev) => ({
                ...prev,
                entity: product.entity,
                tour: {
                    ...prev.tour,
                    productCode: product.productCode,
                    productName: product.name,
                    startTimeLocal: undefined,
                },
                quantities: product.priceOptions?.map((option) => ({ ...option, value: 0 })),
                isPackage: product.allowPackage,
                packageProductNames: product.allowPackage ? product.packageProductNames : undefined,
            }),
            isMounted
        );
        if (product.allowPackage) {
            productController.packageProduct.setData(
                productController.packageProducts.find((pack) => pack?.productCode === product.packageCode),
                isMounted
            );
        }
        // Availability is reset + preloaded (next 7 days) by the ProductsStore's product-change
        // effect, so the new tour's sessions are fetched there — no manual fetch needed here.
    }, [product?.productCode]);

    useEffect(() => {
        bookingController.setBooking(
            (prev) => ({
                ...prev,
                tour: {
                    ...prev.tour,
                    pickUpLocation: productController.product?.pickupList?.[0]?.locationName,
                },
            }),
            isMounted
        );
    }, [productController.product?.pickupList]);

    // Leave the stores clean when navigating away from the home page.
    useEffect(() => {
        return () => {
            bookingController.clearController();
            productController.clearController();
        };
    }, []);

    return (
        <Box component="section" sx={bandSx}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: { xs: 2.5, md: 3.5 } }}>
                    <Typography component="p" sx={eyebrowSx}>
                        Book in seconds
                    </Typography>
                    <Typography component="h2" sx={titleSx}>
                        Check availability &amp; book
                    </Typography>
                    <Typography sx={subtitleSx}>
                        Pick your tour, choose a date and time, and book online with live availability and instant
                        confirmation.
                    </Typography>
                </Box>
                <BookingModal showTourSelect />
            </Container>
        </Box>
    );
}
